import { type FC, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import { useSpecialEvent } from '@/hooks';
import type { Order, HotelPartner } from '@/types';
import { dbToOrder, dbToHotelPartner } from '@/types';
import { CheckCircle, Download, Hotel, Mail, Calendar, MapPin, Phone, ExternalLink, Sparkles, Loader2 } from 'lucide-react';

interface ConfirmationStepProps {
  orderId: string;
  isFreeRegistration?: boolean;
}

/**
 * Fifth and final step of registration wizard - Confirmation
 * Shows order confirmation, hotel booking options, and special event promotion
 */
export const ConfirmationStep: FC<ConfirmationStepProps> = ({ orderId, isFreeRegistration = false }) => {
  const navigate = useNavigate();
  const { specialEvents } = useSpecialEvent();
  // Show the first active special event for promotion
  const specialEvent = specialEvents.length > 0 ? specialEvents[0] : null;
  const [order, setOrder] = useState<Order | null>(null);
  const [hotelPartners, setHotelPartners] = useState<HotelPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrderAndHotels = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch order details
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      if (orderData) {
        setOrder(dbToOrder(orderData));
      }

      // Fetch active hotel partners
      const { data: hotelsData, error: hotelsError } = await supabase
        .from('hotel_partners')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (hotelsError) throw hotelsError;

      if (hotelsData) {
        setHotelPartners(hotelsData.map(dbToHotelPartner));
      }
    } catch (err) {
      console.error('Failed to fetch confirmation data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load confirmation');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Initial fetch
  useEffect(() => {
    void fetchOrderAndHotels();
  }, [fetchOrderAndHotels]);

  // Poll for order status if still pending (webhook may not have processed yet)
  useEffect(() => {
    if (!order || order.paymentStatus === 'completed') {
      return;
    }

    // Poll every 2 seconds for up to 30 seconds
    let pollCount = 0;
    const maxPolls = 15;

    const pollInterval = setInterval(async () => {
      pollCount++;

      const { data } = await supabase
        .from('orders')
        .select('payment_status')
        .eq('id', orderId)
        .single();

      if (data?.payment_status === 'completed') {
        // Refresh all order data
        void fetchOrderAndHotels();
        clearInterval(pollInterval);
      } else if (pollCount >= maxPolls) {
        // Stop polling after max attempts
        clearInterval(pollInterval);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [order, orderId, fetchOrderAndHotels]);

  const handleDownloadReceipt = (): void => {
    // TODO: Generate PDF receipt
    console.log('Download receipt for order:', orderId);
    // Announce to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Receipt download started';
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading confirmation...</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !order) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="rounded-lg bg-destructive/10 p-6 text-destructive">
              <p className="font-semibold">Error loading confirmation</p>
              <p className="mt-2 text-sm">{error || 'Order not found'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isPending = order.paymentStatus !== 'completed';

  return (
    <div className="space-y-6 bg-turquoise-50 -mx-4 px-4 py-6 rounded-lg sm:-mx-0 sm:px-6">
      {/* Success/Processing Message */}
      <Card className={`border-2 ${isPending ? 'border-orange-500 bg-orange-600' : 'border-turquoise-700 bg-turquoise-700'}`}>
        <CardContent className="py-8">
          <div className="text-center" role="status" aria-live="polite">
            {isPending ? (
              <>
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/20 text-white mb-4" aria-hidden="true">
                  <Loader2 className="h-10 w-10 animate-spin" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-viking text-white mb-2">
                  Processing Payment...
                </h2>
                <p className="text-lg text-orange-100">
                  Please wait while we confirm your payment
                </p>
              </>
            ) : (
              <>
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-orange-500 text-white mb-4" aria-hidden="true">
                  <CheckCircle className="h-10 w-10" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-viking text-white mb-2">
                  {isFreeRegistration ? "You're Registered!" : 'Registration Complete!'}
                </h2>
                <p className="text-lg text-orange-100">
                  {isFreeRegistration
                    ? "Welcome to Cactus Cup 2026! We've recorded your registration."
                    : 'Thank you for registering for Cactus Cup 2026'}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Hotel Booking - First Section */}
      {hotelPartners.length > 0 && (
        <Card className="bg-gradient-to-br from-orange-50 to-turquoise-50 border-2 border-orange-200">
          <CardHeader>
            <CardTitle className="font-viking text-xl sm:text-2xl flex items-center gap-2 text-turquoise-800">
              <Hotel className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
              Book Your Hotel
            </CardTitle>
            <p className="text-sm text-turquoise-700">
              We've partnered with these hotels to offer special rates for Cactus Cup attendees
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {hotelPartners.map((hotel) => (
                <div
                  key={hotel.id}
                  className="border border-turquoise-200 rounded-lg p-4 hover:border-turquoise-400 transition-colors bg-white/50"
                >
                  <div className="flex gap-4">
                    {/* Hotel Image */}
                    {hotel.imageUrl && (
                      <div className="flex-shrink-0 hidden sm:block">
                        <img
                          src={hotel.imageUrl}
                          alt={hotel.name}
                          className="w-32 h-24 object-cover rounded-md"
                        />
                      </div>
                    )}

                    {/* Hotel Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <h3 className="font-semibold text-lg text-turquoise-800">{hotel.name}</h3>
                          {hotel.address && (
                            <p className="text-sm text-turquoise-700 flex items-start gap-1 mt-1">
                              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-orange-500" />
                              <span>{hotel.address}</span>
                            </p>
                          )}
                        </div>
                      </div>

                      {hotel.description && (
                        <p className="text-sm text-turquoise-700 line-clamp-2 mb-3">
                          {hotel.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-3">
                        {hotel.phone && (
                          <a
                            href={`tel:${hotel.phone}`}
                            className="text-sm text-turquoise-700 hover:text-turquoise-900 flex items-center gap-1"
                          >
                            <Phone className="h-4 w-4 text-orange-500" />
                            {hotel.phone}
                          </a>
                        )}
                        {hotel.distance && (
                          <span className="text-sm text-turquoise-700">
                            {hotel.distance} from venue
                          </span>
                        )}
                      </div>

                      {hotel.bookingCode && (
                        <div className="mt-3 p-2 bg-turquoise-100/50 rounded text-sm">
                          <span className="text-turquoise-700">Booking Code: </span>
                          <span className="font-mono font-semibold text-turquoise-800">{hotel.bookingCode}</span>
                        </div>
                      )}

                      {hotel.bookingUrl && (
                        <div className="mt-3">
                          <Button
                            onClick={() => window.open(hotel.bookingUrl!, '_blank')}
                            variant="outline"
                            size="sm"
                            className="w-full sm:w-auto border-turquoise-300 text-turquoise-800 hover:bg-turquoise-100"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Book Now
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {hotel.amenities && hotel.amenities.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-turquoise-200">
                      <p className="text-xs text-turquoise-600 mb-2">Amenities:</p>
                      <div className="flex flex-wrap gap-2">
                        {hotel.amenities.map((amenity, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 bg-turquoise-100 text-turquoise-700 rounded-full"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-turquoise-100/50 rounded-lg">
              <p className="text-sm text-turquoise-700">
                <strong className="text-turquoise-800">Tip:</strong> Be sure to mention "Cactus Cup 2026" when booking to receive
                the special rate. Book early as rooms may sell out!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Order Details - Second Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-viking text-xl sm:text-2xl">
            {isFreeRegistration ? 'Registration Details' : 'Order Details'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {isFreeRegistration ? 'Registration Number' : 'Order Number'}
              </p>
              <p className="font-mono font-semibold text-lg">{order.orderNumber}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {isFreeRegistration ? 'Registration Date' : 'Order Date'}
              </p>
              <p className="font-semibold">
                {new Date(order.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-semibold capitalize">
                {isFreeRegistration ? 'Confirmed' : order.paymentStatus}
              </p>
            </div>
            {!isFreeRegistration && (
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="font-semibold text-lg">${(order.total / 100).toFixed(2)}</p>
              </div>
            )}
            {isFreeRegistration && (
              <div>
                <p className="text-sm text-muted-foreground">Entry Type</p>
                <p className="font-semibold">Free Entry</p>
              </div>
            )}
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              A confirmation email has been sent to your registered email address
            </p>
            <p className="text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 inline mr-1" />
              You can view your registration details in your dashboard
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
            {!isFreeRegistration && (
              <Button
                variant="outline"
                onClick={handleDownloadReceipt}
                className="w-full sm:flex-1 order-2 sm:order-1"
                aria-label="Download order receipt as PDF"
              >
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                Download Receipt
              </Button>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              className={`w-full ${isFreeRegistration ? '' : 'sm:flex-1'} order-1 sm:order-2`}
              aria-label="Go to your dashboard"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Special Event Promotion - Third Section */}
      {specialEvent && (
        <Card className="bg-gradient-to-br from-orange-50 to-turquoise-50 border-2 border-orange-200">
          <CardHeader>
            <CardTitle className="font-viking text-xl sm:text-2xl flex items-center gap-2 text-turquoise-800">
              <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
              {specialEvent.title}
            </CardTitle>
            {specialEvent.subtitle && (
              <p className="text-sm text-turquoise-700">
                {specialEvent.subtitle}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {specialEvent.heroImageUrl && (
              <div className="rounded-lg overflow-hidden">
                <img
                  src={specialEvent.heroImageUrl}
                  alt={specialEvent.title}
                  className="w-full h-48 object-cover"
                />
              </div>
            )}

            <div>
              <p className="text-turquoise-700">{specialEvent.description}</p>
            </div>

            {specialEvent.date && (
              <div className="flex items-center gap-2 text-sm text-turquoise-700">
                <Calendar className="h-4 w-4 text-orange-500" />
                <span className="font-medium">
                  {new Date(specialEvent.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                {specialEvent.startTime && (
                  <span className="text-turquoise-600">
                    at {specialEvent.startTime}
                  </span>
                )}
              </div>
            )}

            {specialEvent.location && (
              <div className="flex items-start gap-2 text-sm text-turquoise-700">
                <MapPin className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>{specialEvent.location}</span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => navigate('/special-event')}
              >
                Learn More
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="font-viking text-lg sm:text-xl">What's Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Check Your Email</p>
                <p className="text-sm text-muted-foreground">
                  You'll receive a confirmation email with all your registration details
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Prepare for the Event</p>
                <p className="text-sm text-muted-foreground">
                  Review the event schedule and tournament rules in your dashboard
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Book Your Accommodation</p>
                <p className="text-sm text-muted-foreground">
                  Reserve your hotel room soon to secure the special rate
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Join the Community</p>
                <p className="text-sm text-muted-foreground">
                  Connect with other participants on our Discord server
                </p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
